/**
 * Date validation utilities
 */

/**
 * Validates that a date of birth is:
 * - Not in the future
 * - Age must be at least 18 years
 * - Not more than 110 years in the past
 * @param dateString - Date string in YYYY-MM-DD format (for date inputs) or any valid date format
 * @returns Object with isValid boolean and error message
 */
export function validateDateOfBirth(dateString: string): { isValid: boolean; error?: string } {
  if (!dateString || dateString.trim() === '') {
    return { isValid: false, error: 'Date of birth is required' }
  }

  // Parse date parts manually to avoid UTC timezone shift
  // new Date('YYYY-MM-DD') parses as UTC, causing issues in positive timezones
  let date: Date
  const parts = dateString.trim().split('-')
  if (parts.length === 3) {
    date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10))
  } else {
    date = new Date(dateString)
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return { isValid: false, error: 'Please enter a valid date' }
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to start of day for accurate comparison

  const inputDate = new Date(date)
  inputDate.setHours(0, 0, 0, 0)

  // Check if date is in the future
  if (inputDate > today) {
    return { isValid: false, error: 'Date of birth cannot be in the future' }
  }

  // Calculate age
  const age = today.getFullYear() - inputDate.getFullYear()
  const monthDiff = today.getMonth() - inputDate.getMonth()
  const dayDiff = today.getDate() - inputDate.getDate()

  // Adjust age if birthday hasn't occurred this year
  const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age

  // Check if age is less than 18 years
  if (actualAge < 18) {
    return { isValid: false, error: 'You must be at least 18 years old' }
  }

  // Check if date is more than 110 years in the past
  if (actualAge > 110) {
    return { isValid: false, error: 'Date of birth cannot be more than 110 years ago' }
  }

  return { isValid: true }
}

/**
 * Get minimum date for date of birth input.
 * Matches Angular: getCenturyFormat(1) => Jan 1, 1900 (resident-application-add [min]="oldCenturyDate").
 * @returns Date string in YYYY-MM-DD format
 */
export function getMinDateOfBirth(): string {
  return '1900-01-01'
}

/**
 * Get maximum date for date of birth input (today).
 * Uses local date parts to avoid timezone shift (toISOString is UTC).
 * @returns Date string in YYYY-MM-DD format (local date)
 */
export function getMaxDateOfBirth(): string {
  const today = new Date()
  const y = today.getFullYear()
  const m = String(today.getMonth() + 1).padStart(2, '0')
  const d = String(today.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Parse YYYY-MM-DD string as local date (avoids new Date('YYYY-MM-DD') UTC parsing).
 */
export function parseLocalDateString(isoDateString: string): Date | null {
  if (!isoDateString || !/^\d{4}-\d{2}-\d{2}$/.test(isoDateString)) return null
  const [y, m, d] = isoDateString.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return isNaN(date.getTime()) ? null : date
}

/**
 * Convert MM/DD/YYYY format to YYYY-MM-DD format for date input
 * @param dateString - Date in MM/DD/YYYY format
 * @returns Date in YYYY-MM-DD format or empty string if invalid
 */
export function convertToDateInputFormat(dateString: string): string {
  if (!dateString) return ''

  // Try to parse MM/DD/YYYY format
  const parts = dateString.split('/')
  if (parts.length === 3) {
    const month = parts[0].padStart(2, '0')
    const day = parts[1].padStart(2, '0')
    const year = parts[2]

    // Validate year is 4 digits
    if (year.length === 4) {
      return `${year}-${month}-${day}`
    }
  }

  // If already in YYYY-MM-DD format, return as is
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString
  }

  return ''
}

/**
 * Convert YYYY-MM-DD format to MM/DD/YYYY format
 * @param dateString - Date in YYYY-MM-DD format
 * @returns Date in MM/DD/YYYY format or empty string if invalid
 */
export function convertToDisplayFormat(dateString: string): string {
  if (!dateString) return ''

  // Try to parse YYYY-MM-DD format
  const parts = dateString.split('-')
  if (parts.length === 3) {
    const year = parts[0]
    const month = parts[1]
    const day = parts[2]
    return `${month}/${day}/${year}`
  }

  // If already in MM/DD/YYYY format, return as is
  if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    return dateString
  }

  return ''
}



